import { authService } from './firebase-auth-service';
import { apiUrl } from '../config/api';
import { Capacitor } from '@capacitor/core';

const absoluteUrlPattern = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i;

const resolveEndpoint = (endpoint: string) => {
  if (!endpoint || absoluteUrlPattern.test(endpoint) || endpoint.startsWith('data:') || endpoint.startsWith('blob:')) {
    return endpoint;
  }

  return apiUrl(endpoint);
};

export async function apiRequest(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET', data?: any) {
  try {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };

    // Attempt to obtain Firebase token (works for both web and native)
    let token: string | null = null;
    let tokenRetrievalMethod = 'none';

    try {
      console.log(`[apiRequest] 🔐 Attempting to get Firebase token for ${method} ${endpoint}`);

      // First attempt: getIdToken
      token = await authService.getIdToken();
      if (token) {
        tokenRetrievalMethod = 'getIdToken()';
        console.log(`[apiRequest] ✅ Got token via getIdToken() for ${endpoint}`);
      }

      if (!token) {
        console.log(`[apiRequest] ⚠️ getIdToken() returned null, trying refreshAuthState...`);
        // Try to refresh auth state and token (helps on native platforms)
        await authService.refreshAuthState();
        token = await authService.getIdToken(true);
        if (token) {
          tokenRetrievalMethod = 'getIdToken(true) after refresh';
          console.log(`[apiRequest] ✅ Got token via refreshAuthState + getIdToken(true) for ${endpoint}`);
        }
      }

      if (!token) {
        console.log(`[apiRequest] ⚠️ Still no token, trying getCurrentUser...`);
        const currentUser = await authService.getCurrentUser();
        console.log(`[apiRequest] Current user status:`, {
          hasUser: !!currentUser,
          hasGetIdToken: currentUser && typeof currentUser.getIdToken === 'function'
        });

        if (currentUser && typeof currentUser.getIdToken === 'function') {
          token = await currentUser.getIdToken(true);
          if (token) {
            tokenRetrievalMethod = 'currentUser.getIdToken(true)';
            console.log(`[apiRequest] ✅ Got token via currentUser.getIdToken(true) for ${endpoint}`);
          }
        } else {
          console.error(`[apiRequest] ❌ No current user or getIdToken method unavailable for ${endpoint}`);
        }
      }
    } catch (error) {
      console.error(`[apiRequest] ❌ CRITICAL: Failed to obtain Firebase token for ${endpoint}:`, {
        error: error instanceof Error ? error.message : String(error),
        errorType: error?.constructor?.name,
        stack: error instanceof Error ? error.stack : undefined
      });
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log(`[apiRequest] ✅ Authorization header added for ${endpoint}`, {
        method: tokenRetrievalMethod,
        tokenLength: token.length,
        tokenPrefix: token.substring(0, 20) + '...'
      });
    } else {
      console.error(`[apiRequest] ❌ CRITICAL: Proceeding WITHOUT auth token for ${endpoint}`, {
        endpoint,
        method,
        attemptedMethods: ['getIdToken()', 'refreshAuthState', 'getCurrentUser']
      });
    }

    const isNative = Capacitor.isNativePlatform();

    const config: RequestInit = {
      method,
      headers,
      credentials: isNative ? 'omit' : 'include',
    };

    if (data && method !== 'GET') {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(resolveEndpoint(endpoint), config);

    if (!response.ok) {
      let errorBody: any = null;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        errorBody = await response.json().catch(() => null);
      } else {
        errorBody = await response.text().catch(() => null);
      }

      // Enhanced error message for 401 auth failures
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      if (response.status === 401) {
        if (errorBody?.error === 'No token provided') {
          errorMessage = 'Authentication failed: No login token was sent with this request. Please refresh the page and try again.';
          console.error(`[apiRequest] 🔒 Authentication Error for ${endpoint}:`, {
            issue: 'Token not included in request',
            hadToken: !!token,
            tokenRetrievalMethod,
            possibleCauses: [
              'Firebase session expired',
              'User logged out in another tab',
              'Token retrieval failed silently',
              'IndexedDB corruption'
            ]
          });
        } else if (errorBody?.error === 'Invalid authentication token') {
          errorMessage = 'Authentication failed: Your login session is invalid. Please sign out and sign back in.';
          console.error(`[apiRequest] 🔒 Authentication Error for ${endpoint}:`, {
            issue: 'Token was invalid/expired',
            tokenWasSent: !!token,
            possibleCauses: [
              'Token expired',
              'Token revoked',
              'Firebase configuration mismatch'
            ]
          });
        } else {
          errorMessage = `Authentication failed: ${errorBody?.error || response.statusText}`;
        }
      } else if (errorBody) {
        // Extract error message from response body for other error types
        if (typeof errorBody === 'string') {
          errorMessage = errorBody;
        } else if (errorBody.error) {
          errorMessage = errorBody.error;
        } else if (errorBody.message) {
          errorMessage = errorBody.message;
        }
      }

      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).body = errorBody;
      (error as any).tokenInfo = { hadToken: !!token, method: tokenRetrievalMethod };
      console.error(`[apiRequest] ❌ Error response for ${endpoint}:`, {
        status: response.status,
        body: errorBody,
        tokenWasSent: !!token,
        tokenMethod: tokenRetrievalMethod
      });
      throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return await response.text();
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
}
